import React, { useEffect, useState } from 'react';

import { faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import axios, { type AxiosResponse } from 'axios';

import { Pagination } from '@shared/components/pagination/pagination';
import { IPagination } from '@shared/types/pagination';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import { MessageHistoryPopover } from '@admin/components/messages/history_popover';
import { ReplyButton } from '@admin/components/messages/reply_button';
import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { Spinner } from '@admin/components/spinner';
import { accountURL, dispatchURL, editLeadURL } from '@admin/config/routes';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useTitle } from '@admin/hooks/use_title';

type MessageThreadFilters = {
  department?: string;
  state?: string;
  page?: number;
};

type MessageThreadRecipient = {
  name: string;
  reply_to_name?: string;
  number?: string;
};

type MessageThread = {
  id: number;
  recipient_id: number;
  recipient_type: 'Account' | 'Lead' | 'Assignment';
  body: string;
  created_at: string;
  updated_at: string;
  unread_count: number;
  attachment_count: number;
  state: string;
  kind: string;
  phone_id: string;
  recipient: MessageThreadRecipient;
  requestPending?: boolean;
  touched?: boolean;
  objection_reason?: string;
};

type ObjectionReason = {
  name: string;
  visibility: string[];
};

type RestPagination = {
  current_page: number;
  next_page?: number;
  prev_page?: number;
  total_pages: number;
  total_count: number;
};

type PaginatedMessageThreads = {
  results: MessageThread[];
  pagination: RestPagination;
};

const MAX_TEXT_DISPLAY = 160;
const ALL_STATES_FILTER = 'all';
const LOAD_ERROR_MESSAGE = 'Unable to load message threads. Please try again.';
const OBJECTION_REASONS_ERROR_MESSAGE = 'Unable to load objection reasons. Please try again.';
const RESOLVE_ERROR_MESSAGE = 'Unable to resolve message thread. Please try again.';
const CLOSE_LEAD_ERROR_MESSAGE = 'Unable to close lead. Please try again.';

const toSharedPagination = (pagination: RestPagination): IPagination => ({
  __typename: 'Pagination',
  currentPage: pagination.current_page,
  nextPage: pagination.next_page ?? null,
  prevPage: pagination.prev_page ?? null,
  totalPages: pagination.total_pages,
  totalCount: pagination.total_count,
});

const cleanupFilters = (filters: MessageThreadFilters): MessageThreadFilters => {
  const cleaned: MessageThreadFilters = {};
  if (filters.department) cleaned.department = filters.department;
  if (filters.state && filters.state !== ALL_STATES_FILTER) cleaned.state = filters.state;
  if (filters.page) cleaned.page = filters.page;
  return cleaned;
};

const setDefaultFilters = (filters: MessageThreadFilters): MessageThreadFilters => {
  if (!filters.department && !filters.state && !filters.page) {
    return { state: 'pending' };
  }
  return filters;
};

const stateFilterSelectValue = (state?: string): string => {
  if (!state || state === ALL_STATES_FILTER) {
    return '';
  }
  return state;
};

const parseFiltersFromSearch = (search: string): MessageThreadFilters => {
  const params = new URLSearchParams(search);
  const filters: MessageThreadFilters = {};

  const department = params.get('department');
  if (department) filters.department = department;

  const state = params.get('state');
  if (state === ALL_STATES_FILTER) {
    filters.state = ALL_STATES_FILTER;
  } else if (state) {
    filters.state = state;
  }

  const page = params.get('page');
  if (page && /^\d+$/.test(page)) {
    filters.page = Number.parseInt(page, 10);
  }

  return setDefaultFilters(filters);
};

const syncFiltersToUrl = (filters: MessageThreadFilters) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams();

  if (filters.department) searchParams.set('department', filters.department);
  if (filters.state) searchParams.set('state', filters.state);
  if (filters.page) searchParams.set('page', String(filters.page));

  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
};

const truncateText = (text: string, maxLength: number): string =>
  text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

const formatTimeAgo = (dateString: string): string => {
  try {
    return DateTime.fromISO(dateString).toRelative() ?? '';
  } catch {
    return '';
  }
};

const isPending = (messageThread: MessageThread): boolean => messageThread.state === 'pending';

const isPendingAccount = (messageThread: MessageThread): boolean =>
  isPending(messageThread) && messageThread.recipient_type === 'Account';

const isPendingLead = (messageThread: MessageThread): boolean =>
  isPending(messageThread) && messageThread.recipient_type === 'Lead';

const isOverdue = (messageThread: MessageThread): boolean => {
  const hours = DateTime.now().diff(DateTime.fromISO(messageThread.created_at), 'hours').hours;
  return messageThread.kind === 'inbound' && isPending(messageThread) && hours > 5;
};

const resolveButtonPresent = (messageThread: MessageThread): boolean =>
  isPendingAccount(messageThread) && messageThread.unread_count > 0 && !messageThread.attachment_count;

const closeButtonPresent = (messageThread: MessageThread): boolean => isPendingLead(messageThread);

const recipientURL = (recipientType: MessageThread['recipient_type'], recipientId: number): string => {
  if (recipientType === 'Account') return accountURL({ id: recipientId });
  if (recipientType === 'Lead') return editLeadURL({ id: recipientId });
  return dispatchURL({ id: recipientId });
};

const afterResolve = (messageThread: MessageThread): MessageThread => ({
  ...messageThread,
  requestPending: false,
  state: 'resolved',
  unread_count: 0,
  touched: true,
});

const useMessageThreadsRequest = ({ department, state, page }: MessageThreadFilters) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<PaginatedMessageThreads>> | undefined>();

  useEffect(() => {
    syncFiltersToUrl({ department, state, page });
    setRequest(
      axios.get<PaginatedMessageThreads>('/message_threads.json', {
        params: cleanupFilters({ department, state, page }),
      }),
    );
  }, [department, state, page]);

  return useAxiosRequest(request);
};

const useObjectionReasonsRequest = () => {
  const [request, setRequest] = useState<Promise<AxiosResponse<ObjectionReason[]>> | undefined>();

  useEffect(() => {
    setRequest(axios.get<ObjectionReason[]>('/objection_reasons.json'));
  }, []);

  return useAxiosRequest(request);
};

export const SmsInbox: React.FC = () => {
  useTitle('SMS Inbox');
  const [filters, setFilters] = useState<MessageThreadFilters>(() => parseFiltersFromSearch(window.location.search));
  const { loading: threadsLoading, response: threadsResponse, error: threadsError } = useMessageThreadsRequest(filters);
  const { response: objectionReasonsResponse, error: objectionReasonsRequestError } = useObjectionReasonsRequest();
  const [messageThreads, setMessageThreads] = useState<MessageThread[] | undefined>();
  const [pagination, setPagination] = useState<RestPagination | undefined>();
  const [actionError, setActionError] = useState<string | undefined>();
  const { confirm, confirmation } = useConfirmation();

  const objectionReasons = (objectionReasonsResponse?.data ?? []).filter((reason) => reason.visibility.includes('sms'));
  const loadError = threadsError ? LOAD_ERROR_MESSAGE : undefined;
  const objectionReasonsError = objectionReasonsRequestError ? OBJECTION_REASONS_ERROR_MESSAGE : undefined;

  useEffect(() => {
    setMessageThreads(undefined);
    setPagination(undefined);
  }, [filters.department, filters.state, filters.page]);

  useEffect(() => {
    if (threadsResponse?.data) {
      setMessageThreads(threadsResponse.data.results);
      setPagination(threadsResponse.data.pagination);
    }
  }, [threadsResponse]);

  const updateThread = (threadID: number, updater: (thread: MessageThread) => MessageThread) => {
    setMessageThreads((current) => current?.map((thread) => (thread.id === threadID ? updater(thread) : thread)));
  };

  const onFilterChange = (nextFilters: MessageThreadFilters) => {
    setActionError(undefined);
    setFilters({ ...nextFilters, page: undefined });
  };

  const onPage = (page: number) => {
    setFilters((current) => ({ ...current, page }));
  };

  const resolveMessage = async (messageThread: MessageThread) => {
    const confirmed = await confirm({
      title: 'Are you sure?',
      description: 'Are you sure you want to resolve this message thread?',
      confirm: 'Resolve!',
      cancel: 'Cancel',
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    updateThread(messageThread.id, (thread) => ({ ...thread, requestPending: true }));
    setActionError(undefined);

    try {
      await axios.put('/message_threads/resolve.json', {
        recipient_id: messageThread.recipient_id,
        recipient_type: messageThread.recipient_type,
      });
      updateThread(messageThread.id, afterResolve);
    } catch {
      updateThread(messageThread.id, (thread) => ({ ...thread, requestPending: false }));
      setActionError(RESOLVE_ERROR_MESSAGE);
    }
  };

  const closeLead = async (messageThread: MessageThread) => {
    if (!messageThread.objection_reason) return;

    updateThread(messageThread.id, (thread) => ({ ...thread, requestPending: true }));
    setActionError(undefined);

    try {
      await axios.post(`/messages/${messageThread.id}/objections.json`, {
        message_id: messageThread.id,
        objection_reason: messageThread.objection_reason,
        recipient_id: messageThread.recipient_id,
        recipient_type: messageThread.recipient_type,
      });
      updateThread(messageThread.id, afterResolve);
    } catch {
      updateThread(messageThread.id, (thread) => ({ ...thread, requestPending: false }));
      setActionError(CLOSE_LEAD_ERROR_MESSAGE);
    }
  };

  return (
    <Panel className="message-threads">
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Panel.Header className="mar-btm">
        <div className="mar-all pad-all">
          <div className="row">
            <div className="col-sm-1">
              <span id="sms-inbox-filter-label">Filter by:</span>
            </div>
            <div className="col-sm-2">
              <label className="sr-only" htmlFor="sms-inbox-department-filter">
                Department
              </label>
              <select
                id="sms-inbox-department-filter"
                name="department"
                aria-labelledby="sms-inbox-filter-label sms-inbox-department-filter"
                value={filters.department ?? ''}
                onChange={(event) => onFilterChange({ ...filters, department: event.target.value || undefined })}
                className="form-control"
              >
                <option value=""> - Department - </option>
                <option value="sales">Onboarding Support</option>
                <option value="care">Customer Support</option>
              </select>
            </div>
            <div className="col-sm-2">
              <label className="sr-only" htmlFor="sms-inbox-state-filter">
                State
              </label>
              <select
                id="sms-inbox-state-filter"
                name="state"
                aria-labelledby="sms-inbox-filter-label sms-inbox-state-filter"
                value={stateFilterSelectValue(filters.state)}
                onChange={(event) =>
                  onFilterChange({
                    ...filters,
                    state: event.target.value === '' ? ALL_STATES_FILTER : event.target.value,
                  })
                }
                className="form-control"
              >
                <option value=""> - All - </option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="responded">Responded</option>
              </select>
            </div>
          </div>
        </div>
      </Panel.Header>
      <Panel.Body>
        {loadError && (
          <div className="alert alert-danger" role="alert">
            {loadError}
          </div>
        )}
        {objectionReasonsError && (
          <div className="alert alert-danger" role="alert">
            {objectionReasonsError}
          </div>
        )}
        {actionError && (
          <div className="alert alert-danger" role="alert">
            {actionError}
          </div>
        )}
        <div className="show-overflow-x table-responsive">
          <table className="table table-striped message-threads-table">
            <thead>
              <tr>
                <th className="time-ago-column text-left">Last Updated</th>
                <th className="id-column text-left">ID</th>
                <th className="name-column text-left">Name</th>
                <th className="text-left">Message</th>
                <th className="status-column text-left">Status</th>
                <th className="objection-column text-left">Reason</th>
                <th className="button-column text-left" />
              </tr>
            </thead>
            <tbody>
              {messageThreads?.map((messageThread) => (
                <tr key={messageThread.id}>
                  <td className="text-left">
                    <span>{formatTimeAgo(messageThread.updated_at)}</span>
                    {isOverdue(messageThread) && <label className="label label-danger">overdue</label>}
                  </td>
                  <td className="text-left">
                    <a
                      className="underline"
                      href={recipientURL(messageThread.recipient_type, messageThread.recipient_id)}
                    >
                      {messageThread.recipient_id}
                    </a>
                  </td>
                  <td className="text-left">{messageThread.recipient.name}</td>
                  <td className={`message-body-container text-left ${messageThread.kind} ${messageThread.state}`}>
                    <div className="space-between-flex-container align-items-center">
                      <div className="message-body">{truncateText(messageThread.body, MAX_TEXT_DISPLAY)}</div>
                      <div className="align-items-center mar-lft space-between-flex-container">
                        {messageThread.unread_count > 0 && (
                          <span className="label label-success">{messageThread.unread_count}</span>
                        )}
                        {messageThread.attachment_count > 0 && (
                          <span className="attachment-icon">
                            <FontAwesomeIcon icon={faPaperclip} size="lg" />
                          </span>
                        )}
                        <span className="mar-lft">
                          <MessageHistoryPopover
                            recipientType={messageThread.recipient_type}
                            recipientId={String(messageThread.recipient_id)}
                          />
                        </span>
                        {isPending(messageThread) && messageThread.recipient.number && (
                          <span className="mar-lft">
                            <PhoneFormatter number={messageThread.recipient.number} />
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="message-state">{messageThread.state}</span>
                  </td>
                  <td>
                    {isPendingLead(messageThread) && (
                      <select
                        name="message-objection"
                        disabled={messageThread.requestPending}
                        value={messageThread.objection_reason ?? ''}
                        onChange={(event) =>
                          updateThread(messageThread.id, (thread) => ({
                            ...thread,
                            objection_reason: event.target.value,
                          }))
                        }
                      >
                        <option value="">Select Option</option>
                        {objectionReasons.map((reason) => (
                          <option key={reason.name} value={reason.name}>
                            {reason.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="text-right">
                    {resolveButtonPresent(messageThread) && (
                      <button
                        className="btn btn-primary main-btn mar-rgt"
                        onClick={() => void resolveMessage(messageThread)}
                        disabled={messageThread.requestPending}
                        type="button"
                      >
                        Mark Resolved
                      </button>
                    )}
                    {closeButtonPresent(messageThread) && (
                      <button
                        className="btn btn-primary main-btn mar-rgt"
                        onClick={() => void closeLead(messageThread)}
                        disabled={messageThread.requestPending || !messageThread.objection_reason}
                        type="button"
                      >
                        Close Lead
                      </button>
                    )}
                    {!messageThread.touched && (
                      <ReplyButton
                        disabled={!!messageThread.requestPending}
                        recipientType={messageThread.recipient_type}
                        recipientId={String(messageThread.recipient_id)}
                        phoneId={messageThread.phone_id}
                        replyToName={messageThread.recipient.reply_to_name}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {messageThreads && messageThreads.length === 0 && !loadError && (
          <div className="text-center">No message threads found.</div>
        )}
        {!messageThreads && threadsLoading && !loadError && <Spinner />}
        {pagination && messageThreads && !loadError && (
          <Pagination pagination={toSharedPagination(pagination)} onPage={onPage} />
        )}
      </Panel.Body>
    </Panel>
  );
};
